import { BlockHistory } from '@reliance/types/models/Block';
import {
  ColumnFiltersState,
  createColumnHelper,
  type PaginationState,
  type SortingState,
} from '@tanstack/react-table';
import type { OnChangeFn } from '@tanstack/react-table';
import moment from 'moment';
import React, { useMemo } from 'react';
import { formatBlockType } from '../format/BlockType';
import { formatDate } from '../format/DateTime';
import { formatPhoneNumber } from '../format/PhoneNumber';
import BaseHtmlTable from '../table/BaseHtmlTable';

export type BlockRow = BlockHistory & {
  type?: string;
};

type AccountTableProps = {
  variant: 'account';
  loading: boolean;
  blocks: BlockRow[];
};

type FacilityTableProps = {
  variant: 'facility';
  loading: boolean;
  blocks: BlockRow[];
  page: number;
  totalPages: number;
  total: number;
  perPage: number;
  serverPagination: PaginationState;
  onServerPaginationChange: OnChangeFn<PaginationState>;
  serverSorting: SortingState;
  onServerSortingChange: OnChangeFn<SortingState>;
  serverColumnFilters: ColumnFiltersState;
  onServerColumnFiltersChange: OnChangeFn<ColumnFiltersState>;
};

export type BlockTableProps = AccountTableProps | FacilityTableProps;

const BLOCK_TYPE_FILTER_OPTIONS = [
  { value: '', label: '' },
  { value: '0', label: 'Override' },
  { value: '1', label: 'Collect Block' },
  { value: '2', label: 'Recording Block' },
  { value: '3', label: 'Country Block' },
  { value: '4', label: 'Range Block' },
];

function mapAccountRows(blocks: BlockRow[]) {
  return blocks.map((b) => ({
    ...b,
    blockDate: formatDate(b.blockDate),
    unblockDate: b.unblockDate ? formatDate(b.unblockDate) : '',
    facility: b.facility ? `${b.facility.name} (${b.facility.city}, ${b.facility.state})` : '',
    type: b.type === 'DirectBill' ? formatBlockType(b.blockType) : b.type,
    active:
      b.unblockDate == null ||
      moment(new Date(b.unblockDate.date)).isAfter(moment().add(new Date().getTimezoneOffset().toString(), 'm'))
        ? 'Active'
        : '',
  }));
}

export type FacilityBlockTableRow = {
  blockDate: string;
  unblockDate: string;
  phoneNumber: string;
  reason: string;
  requestedBy: string;
  type: string;
};

function mapFacilityRows(blocks: BlockRow[]): FacilityBlockTableRow[] {
  return blocks.map((b) => ({
    blockDate: formatDate(b.blockDate),
    unblockDate: b.unblockDate ? formatDate(b.unblockDate) : '',
    phoneNumber: b.account?.phoneNumber != null ? formatPhoneNumber(b.account.phoneNumber) : '',
    reason: b.reason ?? '',
    requestedBy: b.requestedBy ?? '',
    type: b.type === 'DirectBill' ? formatBlockType(b.blockType) : (b.type ?? ''),
  }));
}

const BlockTable: React.FC<BlockTableProps> = (props) => {
  if (props.variant === 'account') {
    const rows = useMemo(() => mapAccountRows(props.blocks), [props.blocks]);
    const columns = [
      { key: 'blockDate', name: 'Block Date' },
      { key: 'unblockDate', name: 'Unblock Date' },
      { key: 'type', name: 'Type' },
      { key: 'facility', name: 'Facility' },
      { key: 'requestedBy', name: 'Requested By' },
      { key: 'reason', name: 'Block Reason' },
      { key: 'unblockedByName', name: 'Unblocked By' },
      { key: 'unblockReason', name: 'Unblock Reason' },
      { key: 'active', name: 'Active' },
    ];
    return (
      <BaseHtmlTable
        columns={columns}
        rows={rows}
        loading={props.loading}
        perPage={1000}
      />
    );
  }

  const rows = useMemo(() => mapFacilityRows(props.blocks), [props.blocks]);

  const columnDefs = useMemo(() => {
    const columnHelper = createColumnHelper<FacilityBlockTableRow>();
    return [
      columnHelper.accessor('blockDate', {
        header: () => 'Block Date',
        meta: {
          sortName: 'blockDate',
          filterComponent: 'DateRangeFilter',
        },
      }),
      columnHelper.accessor('unblockDate', {
        header: () => 'Unblock Date',
        meta: {
          sortName: 'unblockDate',
          filterComponent: 'DateRangeFilter',
        },
      }),
      columnHelper.accessor('phoneNumber', {
        header: () => 'Phone Number',
        meta: {
          sortName: 'account.phoneNumber',
          filterComponent: 'TextFilter',
        },
      }),
      columnHelper.accessor('reason', {
        header: () => 'Reason',
        meta: {
          sortName: 'reason',
          filterComponent: 'TextFilter',
        },
      }),
      columnHelper.accessor('requestedBy', {
        header: () => 'Requested By',
        meta: {
          sortName: 'requestedBy',
          filterComponent: 'TextFilter',
        },
      }),
      columnHelper.accessor('type', {
        id: 'typeId',
        header: () => 'Type',
        meta: {
          sortName: 'typeId',
          filterComponent: 'SelectFilter',
          filterOptions: BLOCK_TYPE_FILTER_OPTIONS,
        },
      }),
    ];
  }, []);

  return (
    <BaseHtmlTable
      columnDefs={columnDefs}
      rows={rows}
      loading={props.loading}
      paginationMode="server"
      page={props.page}
      totalPages={props.totalPages}
      total={props.total}
      perPage={props.perPage}
      serverPagination={props.serverPagination}
      onServerPaginationChange={props.onServerPaginationChange}
      serverSorting={props.serverSorting}
      onServerSortingChange={props.onServerSortingChange}
      serverColumnFilters={props.serverColumnFilters}
      onServerColumnFiltersChange={props.onServerColumnFiltersChange}
      pageSizeOptions={[100, 500, 1000]}
    />
  );
};

export default BlockTable;
